import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useI18n } from '../i18n';
import { FieldError } from './fields';
import { Icon } from './icon';
import { CircularProgress, errorMessage } from './index';

// A value changed where it is shown (TF-172): click it (or its pencil, or Enter on the pencil) and it
// becomes its editor in place. Enter or a click elsewhere saves, Esc cancels, Tab saves and opens the
// next value's editor in the same group (data-inline-group). A save that is refused keeps the editor
// open with the server's words under it. A value that cannot be changed is plain text, no outline.
//
// Editors are the app's own fields. A choice (a menu, a picker, a date, a checkbox) saves as soon as
// it is made, with `commit(next)`; typed text saves on Enter, Tab or a click elsewhere, with what was
// typed. Menus and pickers open in layers outside the field: a click in one is not "elsewhere".

export interface InlineEditorProps<T> {
  id: string;
  label: string;
  value: T;
  setValue: (next: T) => void;
  /** Save now: the value given, or what is in the editor. */
  commit: (next?: T) => void;
  busy: boolean;
}

/** The layers a field opens outside itself: a click there is part of editing, not a click elsewhere. */
const LAYERS = '[role="listbox"], .picker-list, [role="dialog"], [role="menu"], .popover, [data-radix-popper-content-wrapper]';
const FOCUSABLE = 'input:not([type="hidden"]):not([hidden]), select, textarea, button:not([hidden]):not([tabindex="-1"]), [tabindex="0"]';

export function InlineField<T>({
  name,
  label,
  display,
  value,
  editable,
  editor,
  onSave,
  same = Object.is,
}: {
  /** Which value it is, within its group: Tab finds the next one by it. */
  name: string;
  /** What the value is called: "Change amount" for the pencil. */
  label: string;
  /** The value as it reads. */
  display: ReactNode;
  value: T;
  editable: boolean;
  editor: (props: InlineEditorProps<T>) => ReactNode;
  /** Saves one value; refused, it throws and the editor stays open. */
  onSave: (next: T) => Promise<unknown>;
  /** Whether two values are the same: nothing is sent for an unchanged one. */
  same?: (a: T, b: T) => boolean;
}) {
  const { t } = useI18n();
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // The latest draft, for a save started by a click elsewhere (a listener, not a render).
  const latest = useRef(draft);
  latest.current = draft;

  const open = () => {
    setDraft(value);
    setError(undefined);
    setEditing(true);
  };
  const close = () => {
    setEditing(false);
    setError(undefined);
  };

  /** Saves; `then` is what comes after a save that went through. */
  const save = async (next: T = latest.current, then?: () => void) => {
    if (busy) return;
    setDraft(next);
    if (same(next, value)) {
      close();
      then?.();
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await onSave(next);
      close();
      then?.();
    } catch (failure) {
      // A value the editor can tell is wrong says so in its own words; the server's refusal in its.
      setError(failure instanceof FieldError ? failure.message : errorMessage(failure, t('common.somethingWrong')));
    } finally {
      setBusy(false);
    }
  };

  /** The next value in the same group that can be changed, opened after this one is saved. */
  const openNext = () => {
    const group = box.current?.closest('[data-inline-group]');
    if (!group) return;
    const fields = [...group.querySelectorAll<HTMLElement>('[data-inline-name][data-editable]')];
    const after = fields[fields.findIndex((field) => field.dataset.inlineName === name) + 1]?.dataset.inlineName;
    if (!after) return;
    requestAnimationFrame(() => group.querySelector<HTMLButtonElement>(`[data-inline-name="${CSS.escape(after)}"] .inline-edit`)?.click());
  };

  // A click elsewhere saves, unless it is in a layer the editor opened.
  useEffect(() => {
    if (!editing) return;
    const elsewhere = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target || box.current?.contains(target) || target.closest(LAYERS)) return;
      void save();
    };
    document.addEventListener('pointerdown', elsewhere, true);
    return () => document.removeEventListener('pointerdown', elsewhere, true);
  });

  // Into the editor's first field when it opens.
  useEffect(() => {
    if (editing) box.current?.querySelector<HTMLElement>('input, select, textarea, button[role="combobox"], [tabindex="0"]')?.focus();
  }, [editing]);

  const keys = (event: KeyboardEvent) => {
    // A menu or a calendar open inside the editor has the keys to itself (React brings its events here).
    if ((event.target as Element).closest(LAYERS)) return;
    if (event.key === 'Escape') {
      // A menu open inside the editor closes first; it stops the key itself.
      event.stopPropagation();
      close();
      requestAnimationFrame(() => box.current?.querySelector<HTMLButtonElement>('.inline-edit')?.focus());
    } else if (event.key === 'Tab' && !event.shiftKey) {
      // Within an editor of two fields (an amount and its currency), Tab goes to the second one first.
      const inside = [...(box.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
      if (inside.indexOf(event.target as HTMLElement) < inside.length - 1) return;
      event.preventDefault();
      void save(undefined, openNext);
    }
  };

  if (!editable) return <div className="inline-field">{display}</div>;
  if (!editing) {
    return (
      <div
        ref={box}
        className="inline-field inline-field-editable"
        data-inline-name={name}
        data-editable
        onClick={(event) => {
          // A link in the value (the account) still goes there.
          if ((event.target as Element).closest('a, button:not(.inline-edit)')) return;
          open();
        }}
      >
        <div className="inline-value">{display}</div>
        <button
          type="button"
          className="inline-edit icon-btn"
          aria-label={t('inline.change', { name: label })}
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          <Icon name="edit" size={18} />
        </button>
      </div>
    );
  }
  return (
    <div ref={box} className="inline-field inline-field-editing" data-inline-name={name} data-editable onKeyDown={keys}>
      <form
        noValidate
        aria-label={t('inline.change', { name: label })}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {editor({ id: `inline-${id}`, label, value: draft, setValue: setDraft, commit: (next) => void save(next), busy })}
        {/* Enter in a text field submits; nothing else needs a button. */}
        <button type="submit" hidden tabIndex={-1} />
      </form>
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      {busy && (
        <span className="inline-saving" role="status" aria-label={t('inline.saving')}>
          <CircularProgress />
        </span>
      )}
    </div>
  );
}
